import React, { useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import parse from 'html-react-parser';
import ModalPopup from '../../../components/Modal/ModalPopup';
import { useTranslation } from 'react-i18next';
import { requestEmailChange } from '../../../api/email';
import { ApiError, ApiErrorCode } from '../../../api/errors';
import settings from '../../../config/settings';
import styles from './ChangeEmailDialog.module.scss';

enum DialogView {
  SendEmail,
  Verify,
  RateLimit,
  Salesforce,
  Workforce,
  Federation,
}

interface ChangeEmailDialogProps {
  onClose: () => void;
  isFederation: boolean;
  isWorkforce: boolean;
  isEmailChangeAllowed: boolean;
  companyName: string;
  profileEmail: string;
  hasSalesforce: boolean;
  accessToken: string;
  language: string;
}

export const ChangeEmailDialog: React.FC<ChangeEmailDialogProps> = ({
  onClose,
  isFederation,
  isWorkforce,
  isEmailChangeAllowed,
  companyName,
  profileEmail,
  hasSalesforce,
  accessToken,
  language,
}) => {
  const { t } = useTranslation();

  const getInitialView = (): DialogView => {
    if (isWorkforce) return DialogView.Workforce;
    if (isFederation) return DialogView.Federation;
    if (hasSalesforce) return DialogView.Salesforce;
    if (!isEmailChangeAllowed) return DialogView.RateLimit;
    return DialogView.SendEmail;
  };

  const [view, setView] = useState<DialogView>(getInitialView);
  const [sending, setSending] = useState(false);

  const handleSendVerification = async () => {
    setSending(true);
    try {
      await requestEmailChange(language, accessToken);
      setView(DialogView.Verify);
    } catch (err) {
      const apiError = err as ApiError;
      if (apiError?.error_code === ApiErrorCode.CannotChangeDueTo24HrsLimit) {
        setView(DialogView.RateLimit);
      }
    } finally {
      setSending(false);
    }
  };

  const renderDefault = () => (
    <>
      <Grid item xs={12} className={styles.popupHeader}>
        {t('user_profile.change_email.title_default')}
      </Grid>
      <Grid item xs={12}>
        <span className={styles.popupContent}>
          {parse(t('user_profile.change_email.description_default'))}
        </span>
      </Grid>
      <Grid item xs={12} className={styles.popupButtonContainer}>
        <Button variant='outlined' onClick={onClose}>
          {t('button.cancel')}
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={handleSendVerification}
          disabled={sending}
        >
          {t('user_profile.change_email.button_send_verification')}
        </Button>
      </Grid>
    </>
  );

  const renderVerify = () => (
    <>
      <Grid item xs={12} className={styles.popupHeader}>
        {t('user_profile.change_email.title_verify')}
      </Grid>
      <Grid item xs={12}>
        <span className={styles.popupContent}>
          {parse(t('user_profile.change_email.description_verify', { email: profileEmail }))}
        </span>
      </Grid>
      <Grid item xs={12} className={styles.popupButtonContainer}>
        <Button variant='contained' color='primary' onClick={onClose}>
          {t('user_profile.change_email.button_close')}
        </Button>
      </Grid>
    </>
  );

  const renderRateLimit = () => (
    <>
      <Grid item xs={12} className={styles.popupHeader}>
        {t('user_profile.change_email.title_rate_limit')}
      </Grid>
      <Grid item xs={12}>
        <span className={styles.popupContent}>
          {t('user_profile.change_email.description_rate_limit')}
        </span>
      </Grid>
      <Grid item xs={12} className={styles.popupButtonContainer}>
        <Button variant='contained' color='primary' onClick={onClose}>
          {t('user_profile.change_email.button_close')}
        </Button>
      </Grid>
    </>
  );

  const renderSalesforce = () => (
    <>
      <Grid item xs={12} className={styles.popupHeader}>
        {t('user_profile.change_email.title_salesforce')}
      </Grid>
      <Grid item xs={12}>
        <span className={styles.popupContent}>
          {parse(t('user_profile.change_email.description_salesforce'))}
        </span>
      </Grid>
      <Grid item xs={12} className={styles.popupButtonContainer}>
        <Button variant='outlined' onClick={onClose}>
          {t('user_profile.change_email.button_not_now')}
        </Button>
        <Button
          variant='contained'
          color='primary'
          component='a'
          href={settings.supportServiceUrl}
          target='_blank'
          rel='noopener noreferrer'
        >
          {t('user_profile.change_email.button_csc')}
        </Button>
      </Grid>
    </>
  );

  const renderWorkforce = () => (
    <>
      <Grid item xs={12} className={styles.popupHeader}>
        {t('user_profile.change_email.title_workforce')}
      </Grid>
      <Grid item xs={12}>
        <span className={styles.popupContent}>
          {parse(t('user_profile.change_email.description_workforce'))}
        </span>
      </Grid>
      <Grid item xs={12} className={styles.popupButtonContainer}>
        <Button variant='outlined' onClick={onClose}>
          {t('user_profile.change_email.button_not_now')}
        </Button>
        <Button
          variant='contained'
          color='primary'
          component='a'
          href={settings.serviceNowUrl}
          target='_blank'
          rel='noopener noreferrer'
        >
          {t('user_profile.change_email.button_servicenow')}
        </Button>
      </Grid>
    </>
  );

  const renderFederation = () => (
    <>
      <Grid item xs={12} className={styles.popupHeader}>
        {t('user_profile.change_email.title_federation')}
      </Grid>
      <Grid item xs={12}>
        <span className={styles.popupContent}>
          {parse(t('user_profile.change_email.description_federation', { companyName }))}
        </span>
      </Grid>
      <Grid item xs={12} className={styles.popupButtonContainer}>
        <Button variant='contained' color='primary' onClick={onClose}>
          {t('user_profile.change_email.button_close')}
        </Button>
      </Grid>
    </>
  );

  const screenRenderers: Record<DialogView, () => React.ReactNode> = {
    [DialogView.SendEmail]: renderDefault,
    [DialogView.Verify]: renderVerify,
    [DialogView.RateLimit]: renderRateLimit,
    [DialogView.Salesforce]: renderSalesforce,
    [DialogView.Workforce]: renderWorkforce,
    [DialogView.Federation]: renderFederation,
  };

  return (
    <Grid container className={styles.confirmationPopupContainer}>
      <ModalPopup paperClassName={styles.changeEmailPopup}>
        {screenRenderers[view]()}
      </ModalPopup>
    </Grid>
  );
};
